
import {
  CheckCircle2,
  Download,
  FileText,
  Globe,
  Linkedin,
  Video,
} from "lucide-react";

import { mapCandidateDetails } from "@/modules/hrPortal/utils/candidateDetailsMapper";

import {
  getFileSize,
  toText,
} from "@/modules/hrPortal/utils/formatters";

import ApplicationInterviewPanel, {
  type MasterDatabaseInterviewData,
} from "./ApplicationInterviewPanel";

import type { Interview } from "@/modules/hrPortal/types/interview.types";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";

import { Separator } from "@/shared/components/ui/separator";
import { Textarea } from "@/shared/components/ui/textarea";

type CandidateDetailsViewModel =
  ReturnType<typeof mapCandidateDetails>;

type CandidateData = CandidateDetailsViewModel["candidateData"];
type Resume = CandidateDetailsViewModel["resume"];
type Video = CandidateDetailsViewModel["video"];

interface CandidateSidebarProps {
  isMasterDatabaseRecord?: boolean;
  masterDatabaseInterviewData?: MasterDatabaseInterviewData | null;

  candidateData: CandidateData;
  resume: Resume;
  video: Video;
  applicationId: string;
  notes: string;
  onNotesChange: (notes: string) => void;
  onSaveNotes: () => void;

  onDownload: (
    type: "resume" | "video",
    fileName?: string,
  ) => void;

  interviews: Interview[];
  interviewsLoading?: boolean;

  interviewWorkflow: {
    key: string;
    name: string;
    sequence: number;
  }[];

  onStartInterview?: (interviewId: string) => Promise<void>;

  onCancelInterview?: (
    interviewId: string,
    reason: string,
  ) => Promise<void>;

  onNoShowInterview?: (
    interviewId: string,
    reason: string,
  ) => Promise<void>;

  onCompleteInterview?: (
    interviewId: string,
    result: "PASSED" | "FAILED",
    remarks?: string,
  ) => Promise<void>;

  onOpenReschedule?: (interview: Interview) => void;

  /**
   * SH-22: controls the Interview Rounds tab and its actions.
   * Application Status remains visible when this is false.
   */
  canAccessInterviewScheduling?: boolean;
}

const normalizeUrl = (url?: string | null) => {
  if (!url) return "";

  const value = url.trim();

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `https://${value}`;
};

export default function CandidateSidebar({
  candidateData,
  resume,
  video,
  applicationId,
  notes,
  onNotesChange,
  onSaveNotes,
  onDownload,
  interviews,
  interviewsLoading,
  interviewWorkflow,
  onStartInterview,
  onCancelInterview,
  onNoShowInterview,
  onCompleteInterview,
  onOpenReschedule,
  canAccessInterviewScheduling = true,

  // Master Database props
  isMasterDatabaseRecord = false,
  masterDatabaseInterviewData = null,
}: CandidateSidebarProps) {
  return (
    <div className="w-full min-w-0 space-y-4 sm:space-y-5 lg:space-y-6">
      {/* Interview panel */}
      <div className="min-w-0">
        <ApplicationInterviewPanel
          statusHistory={candidateData.statusHistory}
          interviews={interviews}
          interviewsLoading={interviewsLoading}
          interviewWorkflow={interviewWorkflow}
          onStartInterview={onStartInterview}
          onCancelInterview={onCancelInterview}
          onNoShowInterview={onNoShowInterview}
          onCompleteInterview={onCompleteInterview}
          onOpenReschedule={onOpenReschedule}
          canAccessInterviewScheduling={canAccessInterviewScheduling}
          isMasterDatabaseRecord={isMasterDatabaseRecord}
          masterDatabaseInterviewData={masterDatabaseInterviewData}
        />
      </div>

      {/* Quick information */}
      <Card className="w-full min-w-0">
        <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
          <CardTitle className="text-sm font-semibold">
            Quick Information
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3 px-4 pb-4 text-sm sm:px-5 sm:pb-5">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              Source
            </span>

            <Badge
              variant="outline"
              className="max-w-[65%] whitespace-normal break-words text-right"
            >
              {candidateData.source
                ? candidateData.source
                    .toLowerCase()
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                : "N/A"}
            </Badge>
          </div>

          <Separator />

          <div className="flex min-w-0 items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              Applied
            </span>

            <span className="min-w-0 break-words text-right">
              {candidateData.appliedDate || "N/A"}
            </span>
          </div>

          <Separator />

          <div className="flex min-w-0 items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              Experience <span className="text-red-500">*</span>
            </span>

            <span className="min-w-0 break-words text-right">
              {candidateData.experience || "N/A"}
            </span>
          </div>

          <Separator />

          <div className="flex min-w-0 items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              Notice Period <span className="text-red-500">*</span>
            </span>

            <span className="min-w-0 break-words text-right">
              {candidateData.noticePeriod || "N/A"}
            </span>
          </div>

          <Separator />

          <div className="flex min-w-0 items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              Current CTC
            </span>

            <span className="min-w-0 break-words text-right font-medium text-green-600">
              {candidateData.currentSalary || "N/A"}
            </span>
          </div>

          <Separator />

          <div className="flex min-w-0 items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              Expected CTC
            </span>

            <span className="min-w-0 break-words text-right font-medium text-blue-600">
              {candidateData.expectedSalary || "N/A"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card className="w-full min-w-0">
        <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
          <CardTitle className="text-sm font-semibold">
            Documents
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-2 px-4 pb-4 sm:px-5 sm:pb-5">
          <button
            type="button"
            onClick={() =>
              onDownload(
                "resume",
                resume?.fileName ||
                  resume?.originalFileName ||
                  "resume",
              )
            }
            disabled={!resume}
            className="flex w-full min-w-0 items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileText className="h-5 w-5 shrink-0 text-red-500" />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Resume</p>

              <p className="break-words text-xs text-muted-foreground">
                {resume
                  ? `${toText(resume.mimeType)} - ${getFileSize(resume.fileSize)}`
                  : "N/A"}
              </p>
            </div>

            <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>

          <button
            type="button"
            onClick={() =>
              onDownload(
                "video",
                video?.fileName ||
                  video?.originalFileName ||
                  "video",
              )
            }
            disabled={!video}
            className="flex w-full min-w-0 items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Video className="h-5 w-5 shrink-0 text-purple-500" />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                Video Introduction
              </p>

              <p className="break-words text-xs text-muted-foreground">
                {video
                  ? `${toText(video.mimeType)} - ${getFileSize(video.fileSize)}`
                  : "N/A"}
              </p>
            </div>

            <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </CardContent>
      </Card>

      {/* Duplicate check */}
      <Card className="w-full min-w-0 border-green-200 bg-green-50/50">
        <CardContent className="p-4 sm:p-5">
          <div className="mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />

            <h4 className="font-semibold text-green-800">
              Duplicate Check
            </h4>
          </div>

          <p className="break-words text-sm text-green-700">
            No duplicates found based on email, phone, and resume similarity.
          </p>
        </CardContent>
      </Card>

      {/* Online profiles */}
      <Card className="w-full min-w-0">
        <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
          <CardTitle className="text-sm font-semibold">
            Online Profiles
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-2 px-4 pb-4 sm:px-5 sm:pb-5">
          {candidateData.linkedin && (
            <a
              href={normalizeUrl(candidateData.linkedin)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 items-start gap-3 rounded p-2 transition-colors hover:bg-slate-50"
            >
              <Linkedin className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />

              <span className="min-w-0 break-all text-sm">
                {candidateData.linkedin}
              </span>
            </a>
          )}

          {candidateData.portfolio && (
            <a
              href={normalizeUrl(candidateData.portfolio)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 items-start gap-3 rounded p-2 transition-colors hover:bg-slate-50"
            >
              <Globe className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />

              <span className="min-w-0 break-all text-sm">
                {candidateData.portfolio}
              </span>
            </a>
          )}

          {!candidateData.linkedin && !candidateData.portfolio && (
            <p className="text-sm text-muted-foreground">
              No online profiles available.
            </p>
          )}
        </CardContent>
      </Card>

      {/* HR notes */}
      <Card className="w-full min-w-0">
        <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
          <CardTitle className="text-sm font-semibold">
            HR Notes
          </CardTitle>
        </CardHeader>

        <CardContent className="px-4 pb-4 sm:px-5 sm:pb-5">
          <Textarea
            placeholder="Add notes about this candidate..."
            className="min-h-[100px] w-full resize-y"
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
          />

          <Button
            type="button"
            disabled={!applicationId}
            onClick={onSaveNotes}
            className="mt-3 w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save Notes
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
